import {
  Action,
  Form,
  FormRow,
  Option,
  PopupHint,
  SegmentedButton,
  Select,
} from '@op/opds.opux';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import React from 'react';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import ModalForExtremeSports from './ModalForExtremeSports';
import { FormInput } from '../common/Input/FormInput';

const TravelQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();
  const ibRef = React.createRef();

  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });

  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };

  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );

  const travelDetails = state.selectedInsurances[index].questions;

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    //(${insurance.id.replace(insurance.type,'')})
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const handleClick = (event) => {
    event.preventDefault();
    return ibRef.current.open();
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          className={'RS-travel-questions-row'}
          dataKey={`${insurance.id}-insuredItem`}
          label={<Message id="labelText.insuredItem" />}
          labelFor={buildElementId(insurance.id, 'TRAVEL_TYPE')}
          required
        >
          <Select
            value={getValue(travelDetails.travelType)}
            name={`${insurance.id}-travelType`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  travelType: makeInputData(
                    e.target.value,
                    'insuredItem',
                    getOptionTitle(e, 'insuredItem'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
            id={buildElementId(insurance.id, 'TRAVEL_TYPE')}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Me" key="Me">
              {getMessage('option.insuredItem.001')}
            </option>
            <option value="Family member" key="Family member">
              {getMessage('option.insuredItem.002')}
            </option>
            <option value="Luggage only" key="Luggage only">
              {getMessage('option.insuredItem.003')}
            </option>
          </Select>
          <ValidationError
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'TRAVEL_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
      {/* Me questions */}
      {getValue(travelDetails.travelType) === 'Me' && (
        <div>
          <FormRow
            dataKey={`${insurance.id}-longTrip`}
            className={'RS-travel-questions-row'}
            label={<Message id="labelText.longTrip" />}
            labelFor={buildElementId(insurance.id, 'TRAVEL_LONGTRIP')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.longTrip" />
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(travelDetails.longTrip)}
              name={`${insurance.id}-longTrip`}
              onChange={(e) => {
                travelDetails.longTrip = makeInputData(
                  e.target.value,
                  'longTrip',
                  getOptionTitle(e, 'longTrip'),
                  'error.fill.in.missing.information'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_LONGTRIP')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="0-45 days" key="0-45 days">
                {getMessage('option.longTrip.001')}
              </option>
              <option value="46-90 days" key="46-90 days">
                {getMessage('option.longTrip.002')}
              </option>
              <option value="91-120 days" key="91-120 days">
                {getMessage('option.longTrip.003')}
              </option>
              <option value="121-179 days" key="121-179 days">
                {getMessage('option.longTrip.004')}
              </option>
              <option value="179+ days" key="179+ days">
                {getMessage('option.longTrip.005')}
              </option>
            </Select>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'TRAVEL_LONGTRIP')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-competitiveSportsMe`}
            label={<Message id="labelText.competitiveSportsMe" />}
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_COMPETITIVESPORTSME'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.competitiveSports" />
              </PopupHint>
            }
          >
            <SegmentedButton
              value={getValue(travelDetails.competitiveSportsMe)}
              name={`${insurance.id}-competitiveSportsMe`}
              onChange={(e) => {
                travelDetails.competitiveSportsMe = makeInputData(
                  e.target.value,
                  'competitiveSportsMe',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_COMPETITIVESPORTSME')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_COMPETITIVESPORTSME'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(travelDetails.competitiveSportsMe) === 'Yes' && (
            <FormInput
              type="sport"
              inputId={buildElementId(insurance.id, 'TRAVEL_WHICHSPORTSME')}
              value={getValue(travelDetails.whichSportsMe)}
              onChange={(whichSportsMe, error) => {
                travelDetails.whichSportsMe = makeInputData(
                  whichSportsMe,
                  'whichSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: `${insurance.id}-whichSports`,
                  error,
                });
              }}
              label={getMessage('labelText.whichSports')}
              required
            />
          )}
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-highRiskSportsMe`}
            label={
              <div>
                <Message id="labelText.highRiskSportsMe" />
                <br />
                <Action tabIndex="0" inline href="" onClick={handleClick}>
                  {<Message id="labelText.highRiskSportsMeSecond" />}
                </Action>
                <ModalForExtremeSports ibRef={ibRef} />
              </div>
            }
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_HIGHRISKSPORTSME'
            )}
          >
            <SegmentedButton
              value={getValue(travelDetails.highRiskSportsMe)}
              name={`${insurance.id}-highRiskSportsMe`}
              onChange={(e) => {
                travelDetails.highRiskSportsMe = makeInputData(
                  e.target.value,
                  'highRiskSportsMe',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_HIGHRISKSPORTSME')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_HIGHRISKSPORTSME'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(travelDetails.highRiskSportsMe) === 'Yes' && (
            <FormInput
              type="sport"
              inputId={buildElementId(insurance.id, 'TRAVEL_WHICHHIGHRISKSPORTSME')}
              value={getValue(travelDetails.whichHighRiskSportsMe)}
              onChange={(whichHighRiskSportsMe, error) => {
                travelDetails.whichHighRiskSportsMe = makeInputData(
                  whichHighRiskSportsMe,
                  'whichHighRiskSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: `${insurance.id}-whichHighRiskSports`,
                  error,
                });
              }}
              label={getMessage('labelText.whichHighRiskSports')}
              required
            />
          )}
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-luggageInsuranceMe`}
            label={<Message id="labelText.luggageInsuranceMe" />}
            labelFor={buildElementId(insurance.id, 'TRAVEL_LAGUAGEINSURANCEME')}
          >
            <SegmentedButton
              value={getValue(travelDetails.luggageInsuranceMe)}
              name={`${insurance.id}-luggageInsuranceMe`}
              onChange={(e) => {
                travelDetails.luggageInsuranceMe = makeInputData(
                  e.target.value,
                  'luggageInsuranceMe',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'TRAVEL_LAGUAGEINSURANCEME'
                  ),
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_LAGUAGEINSURANCEME')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_LAGUAGEINSURANCEME'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(travelDetails.luggageInsuranceMe) === 'Yes' && (
            <FormRow
              className={'RS-travel-questions-row'}
              dataKey={`${insurance.id}-insuranceIncludesPackages`}
              label={<Message id="labelText.insuranceIncludesPackages" />}
              labelFor={buildElementId(
                insurance.id,
                'TRAVEL_INSURANCEINCLUDESME'
              )}
              hint={
                <PopupHint placement="top">
                  <Message id="hintText.insuranceIncludes" />
                </PopupHint>
              }
              required
            >
              <Select
                value={getValue(travelDetails.insuranceIncludesMe)}
                name={`${insurance.id}-insuranceIncludesMe`}
                onChange={(e) => {
                  travelDetails.insuranceIncludesMe = makeInputData(
                    e.target.value,
                    'insuranceIncludesPackages',
                    getOptionTitle(e, 'insuranceIncludesMe')
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: travelDetails,
                    questionId: e.target.id,
                  });
                }}
                id={buildElementId(insurance.id, 'TRAVEL_INSURANCEINCLUDESME')}
              >
                <option value="choose" key="choose">
                  {getMessage('uiText.choose')}
                </option>
                <option
                  value="Your own luggage only"
                  key="Your own luggage only"
                >
                  {getMessage('option.insuranceIncludesMe.001')}
                </option>
                <option
                  value="The whole family's luggage"
                  key="The whole family's luggage"
                >
                  {getMessage('option.insuranceIncludesMe.002')}
                </option>
              </Select>
              <ValidationError
                requireVisible={false}
                errorId={buildElementId(
                  insurance.id,
                  'TRAVEL_INSURANCEINCLUDESME'
                )}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
          )}
        </div>
      )}

      {/* Family member questions */}
      {getValue(travelDetails.travelType) === 'Family member' && (
        <div>
          <FormInput
            type="string"
            inputId={buildElementId(
              insurance.id,
              'TRAVEL_FIRSTNAMEFAMILYMEMBER'
            )}
            value={getValue(travelDetails.firstNameFamilyMember)}
            onChange={(firstNameFamilyMember, error) => {
              travelDetails.firstNameFamilyMember = makeInputData(
                firstNameFamilyMember,
                'FirstName',
                '',
                `${insurance.caption} : ${getMessage(
                  'labelText.FirstName'
                )} - ${error?.message}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: travelDetails,
                questionId: `${insurance.id}-FirstName`,
                error,
              });
            }}
            label={getMessage('labelText.FirstName')}
            required
          />
          <FormInput
            type="string"
            inputId={buildElementId(
              insurance.id,
              'TRAVEL_LASTNAMEFAMILYMEMBER'
            )}
            value={getValue(travelDetails.lastNameFamilyMember)}
            onChange={(lastNameFamilyMember, error) => {
              travelDetails.lastNameFamilyMember = makeInputData(
                lastNameFamilyMember,
                'LastName'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: travelDetails,
                questionId: `${insurance.id}-LastName`,
                error,
              });
            }}
            label={getMessage('labelText.LastName')}
            required
          />
          <FormInput
            type="ssn-finland"
            inputId={buildElementId(
              insurance.id,
              'TRAVEL_SECURITYIDFAMILYMEMBER'
            )}
            value={getValue(travelDetails.securityIdFamilyMember)}
            onChange={(securityIdFamilyMember, error) => {
              travelDetails.securityIdFamilyMember = makeInputData(
                securityIdFamilyMember.toUpperCase(),
                'SecurityId'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: travelDetails,
                questionId: `${insurance.id}-SecurityId`,
                error,
              });
            }}
            label={getMessage('labelText.SecurityId')}
            required
          />

          <FormRow
            dataKey={`${insurance.id}-longTrip`}
            className={'RS-travel-questions-row'}
            label={<Message id="labelText.longTrip" />}
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_LONGTRIPFAMILYMEMBER'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.longTrip" />
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(travelDetails.longTripFamilyMember)}
              name={`${insurance.id}-longTripFamilyMember`}
              onChange={(e) => {
                travelDetails.longTripFamilyMember = makeInputData(
                  e.target.value,
                  'longTrip',
                  getOptionTitle(e, 'longTrip')
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_LONGTRIPFAMILYMEMBER')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="0-45 days" key="0-45 days">
                {getMessage('option.longTrip.001')}
              </option>
              <option value="46-90 days" key="46-90 days">
                {getMessage('option.longTrip.002')}
              </option>
              <option value="91-120 days" key="91-120 days">
                {getMessage('option.longTrip.003')}
              </option>
              <option value="121-179 days" key="121-179 days">
                {getMessage('option.longTrip.004')}
              </option>
              <option value="179+ days" key="179+ days">
                {getMessage('option.longTrip.005')}
              </option>
            </Select>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_LONGTRIPFAMILYMEMBER'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-competitiveSports`}
            label={<Message id="labelText.competitiveSportsMe" />}
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_COPETITIVESPORTSFAMILYMEMBER'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.competitiveSports" />
              </PopupHint>
            }
          >
            <SegmentedButton
              value={getValue(travelDetails.competitiveSportsFamilyMember)}
              name={`${insurance.id}-competitiveSportsFamilyMember`}
              onChange={(e) => {
                travelDetails.competitiveSportsFamilyMember = makeInputData(
                  e.target.value,
                  'competitiveSports',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'TRAVEL_COPETITIVESPORTSFAMILYMEMBER'
                  ),
                });
              }}
              id={buildElementId(
                insurance.id,
                'TRAVEL_COPETITIVESPORTSFAMILYMEMBER'
              )}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_COPETITIVESPORTSFAMILYMEMBER'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(travelDetails.competitiveSportsFamilyMember) === 'Yes' && (
            <FormInput
              type="sport"
              inputId={buildElementId(
                insurance.id,
                'TRAVEL_WHICHSPORTSFAMILYMEMBER'
              )}
              value={getValue(travelDetails.whichSportsFamilyMember)}
              onChange={(whichSportsFamilyMember, error) => {
                travelDetails.whichSportsFamilyMember = makeInputData(
                  whichSportsFamilyMember,
                  'whichSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: `${insurance.id}-whichSports`,
                  error,
                });
              }}
              label={getMessage('labelText.whichSports')}
              required
            />
          )}
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-highRiskSports`}
            label={
              <div>
                <Message id="labelText.highRiskSportsMe" />
                <br />
                <Action tabIndex="0" inline href="" onClick={handleClick}>
                  {<Message id="labelText.highRiskSportsMeSecond" />}
                </Action>
                <ModalForExtremeSports ibRef={ibRef} />
              </div>
            }
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_HIGHRISKSPORTSFAMILYMEMBER'
            )}
          >
            <SegmentedButton
              value={getValue(travelDetails.highRiskSportsFamilyMember)}
              name={`${insurance.id}-highRiskSportsFamilyMember`}
              onChange={(e) => {
                travelDetails.highRiskSportsFamilyMember = makeInputData(
                  e.target.value,
                  'highRiskSports',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'TRAVEL_HIGHRISKSPORTSFAMILYMEMBER'
                  ),
                });
              }}
              id={buildElementId(
                insurance.id,
                'TRAVEL_HIGHRISKSPORTSFAMILYMEMBER'
              )}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_HIGHRISKSPORTSFAMILYMEMBER'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(travelDetails.highRiskSportsFamilyMember) === 'Yes' && (
            <FormInput
              type="sport"
              inputId={buildElementId(
                insurance.id,
                'TRAVEL_WHICHHIGHRISKSPORTSFAMILYMEMBER'
              )}
              value={getValue(travelDetails.whichHighRiskSportsFamilyMember)}
              onChange={(whichHighRiskSportsFamilyMember, error) => {
                travelDetails.whichHighRiskSportsFamilyMember = makeInputData(
                  whichHighRiskSportsFamilyMember,
                  'whichHighRiskSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: `${insurance.id}-whichHighRiskSports`,
                  error,
                });
              }}
              label={getMessage('labelText.whichHighRiskSports')}
              required
            />
          )}
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-luggageInsuranceFamily`}
            label={<Message id="labelText.luggageInsuranceFamily" />}
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_LUGGAGEINSURANCEFAMILY'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.luggageInsuranceFamily" />
              </PopupHint>
            }
          >
            <SegmentedButton
              value={getValue(travelDetails.luggageInsuranceFamily)}
              name={`${insurance.id}-luggageInsuranceFamily`}
              onChange={(e) => {
                travelDetails.luggageInsuranceFamily = makeInputData(
                  e.target.value,
                  'luggageInsuranceFamily',
                  `option.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'TRAVEL_LUGGAGEINSURANCEFAMILY'
                  ),
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_LUGGAGEINSURANCEFAMILY')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('option.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('option.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_LUGGAGEINSURANCEFAMILY'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
        </div>
      )}

      {/* Luggage only Questions */}
      {getValue(travelDetails.travelType) === 'Luggage only' && (
        <div>
          <FormRow
            className={'RS-travel-questions-row'}
            dataKey={`${insurance.id}-insuranceIncludesPackages`}
            label={<Message id="labelText.insuranceIncludesPackages" />}
            labelFor={buildElementId(
              insurance.id,
              'TRAVEL_INSURANCEINCLUDESME1'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.insuranceIncludes" />
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(travelDetails.insuranceIncludesMe1)}
              name={`${insurance.id}-insuranceIncludesMe1`}
              onChange={(e) => {
                travelDetails.insuranceIncludesMe1 = makeInputData(
                  e.target.value,
                  'insuranceIncludesPackages',
                  getOptionTitle(e, 'insuranceIncludesMe')
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: travelDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'TRAVEL_INSURANCEINCLUDESME1')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="Your own luggage only" key="Your own luggage only">
                {getMessage('option.insuranceIncludesMe.001')}
              </option>
              <option
                value="The whole family's luggage"
                key="The whole family's luggage"
              >
                {getMessage('option.insuranceIncludesMe.002')}
              </option>
            </Select>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'TRAVEL_INSURANCEINCLUDESME1'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
        </div>
      )}
    </Form>
  );
};

export default TravelQuestions;
